<template>
    <div :class="embedded ? null : 'content-wrapper'">
        <div :class="embedded ? null : 'content'">
            <div class="titleBox mb-5">
                Подключение канала
            </div>
            <ChannelSettingsForm
                v-if="channelConnector && providerType && !isTransitioning"
                :value="channelSettings"
                :connector="channelConnector"
                :providerType="providerType"
                :isOnboarding="true"
                :appCreated="true"
                :inSidebar="!redirectOnConnect"
                @input="channelSettings = $event"
                @saved="onChannelConnected"
            />
            <div v-else class="d-flex justify-center">
                <v-progress-circular indeterminate color="mainNew"></v-progress-circular>
            </div>

            <div v-if="showBack && channelConnector" class="mt-6 d-flex">
                <v-btn
                    color="mainNew"
                    class="white--text"
                    :elevation="0"
                    @click="onBack"
                >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Назад
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, getCurrentInstance } from 'vue'
import { useProject } from '@root/src/hooks/useProject'
import useRouter from '@/hooks/useRouter'
import { CHANNEL_TYPES, PROVIDER_TYPES, CONNECTOR_TYPES } from '@/vars/general'
import { createChannel } from '@/api/networkFixer'
import { fetchConnector } from '@/api/projects'
import { refreshProjectAndAssignChannelSlots } from '@/utils/onboardingHelpers'

export default {
    name: 'ChannelConnectionView',
    components: {
        ChannelSettingsForm: () => import('@/components/channels/ChannelSettingsForm.vue'),
    },
    props: {
        channelType: {
            type: String,
            required: false,
            validator: (value) => {
                if (!value) return true // Разрешаем null/undefined
                return Object.values(CHANNEL_TYPES).includes(value)
            },
        },
        /** Если false, не редиректить на проект после подключения (для YC онбординга: сначала канал, потом настройки) */
        redirectOnConnect: {
            type: Boolean,
            default: true,
        },
        /** Показать кнопку "Назад" (для онбординга) */
        showBack: {
            type: Boolean,
            default: false,
        },
        /** Встроенный режим: без оберток content-wrapper/content (чтобы не было вложенности в онбординге) */
        embedded: {
            type: Boolean,
            default: false,
        },
        /** Явно заданный providerType (например, после выбора провайдера на отдельном шаге) */
        providerTypeOverride: {
            type: String,
            required: false,
        },
    },
    emits: ['channel-connected', 'refresh-step', 'back', 'connector-created'],
    setup(props, { emit }) {
        const root = getCurrentInstance().proxy
        const { project } = useProject()
        const router = useRouter()
        const channelConnector = ref(null)
        const channelSettings = ref({})
        const isTransitioning = ref(false)

        // Получаем channelType из props или query параметров
        const currentChannelType = computed(() => {
            if (props.channelType) return props.channelType
            const route = router.router.currentRoute
            return route.query.channelType || null
        })
        
        // Маппинг channelType на providerType
        const providerType = computed(() => {
            if (props.providerTypeOverride) return props.providerTypeOverride
            if (!currentChannelType.value) return null
            
            const channelToProviderMap = {
                [CHANNEL_TYPES.whatsappChannel]: PROVIDER_TYPES.whatsappDh, // По умолчанию whatsappDh (dh-whatsapp)
                [CHANNEL_TYPES.telegramChannel]: PROVIDER_TYPES.telegramDh, // Провайдер 1 (telegramApi) выведен из выбора
                [CHANNEL_TYPES.maxChannel]: PROVIDER_TYPES.maxDh,
                [CHANNEL_TYPES.maxBusiness]: PROVIDER_TYPES.maxBusiness,
                [CHANNEL_TYPES.smsChannel]: PROVIDER_TYPES.smsGate,
            }
            
            return channelToProviderMap[currentChannelType.value] || null
        })
        const onBack = async () => {
            await root.$store.dispatch('project/deleteConnector', {
                connectorId: channelConnector.value?.connectorId,
                skipLoading: true,
            })
            emit('back')
        }

        onMounted(async () => {
            // Используем computed channelType
            const channelType = currentChannelType.value
            
            if (!channelType) {
                root.$store.dispatch('callNotify', 'Ошибка: тип канала не указан')
                return
            }
            
            // Находим или создаем коннектор для канала
            const existingChannel = project.value.connectors.find((c) => {
                if (!c) return false
                if (c.connectorType !== channelType) return false
                // Учитываем провайдера, чтобы не переиспользовать коннектор от другого providerType
                if (props.providerTypeOverride) {
                    return c.providerType === props.providerTypeOverride
                }
                return true
            })

            if (existingChannel) {
                channelConnector.value = existingChannel
                root.$store.commit('project/setLoggedAddingConnector', existingChannel)
            } else {
                // Создаем новый коннектор для канала используя createConnector
                const connectorObj = await root.$store.dispatch('project/createConnector', {
                    projectId: project.value.id,
                    connectorType: channelType,
                    providerType: providerType.value, // Устанавливаем providerType
                })
                
                // Сохраняем в sessionStorage для совместимости
                sessionStorage.setItem('currentAddingConnector', JSON.stringify({
                    type: channelType,
                    name: getChannelName(channelType),
                    maxCount: 1,
                }))
                
                const res = await root.$store.dispatch('project/addConnector', {
                    connector: connectorObj,
                    skipLoading: true,
                })
                if (res.error) {
                    root.$store.dispatch('callNotify', 'Ошибка создания канала')
                    return
                }

                const connectorId = connectorObj.connectorId
                // Сообщаем родителю, что коннектор был создан в рамках этого шага (для возможного отката)
                emit('connector-created', connectorId)
                let updatedConnector = connectorObj

                // Как в сайдбаре: создаём канал в networkFixer и подтягиваем коннектор с channelId
                const channelRes = await createChannel({ connectorId })
                if (!channelRes.error && channelRes.data) {
                    try {
                        const { data: fetchedConnector } = await fetchConnector(connectorId)
                        if (fetchedConnector) {
                            root.$store.commit('updateConnector', fetchedConnector)
                            updatedConnector = fetchedConnector
                        }
                    } catch (e) {
                    }
                }

                await root.$store.dispatch('project/fetchProject', project.value.id)
                const updatedProject = root.$store.getters.projectById(project.value.id)
                const connectorFromProject = updatedProject?.connectors?.find(
                    (c) => c.connectorId === connectorId
                )
                channelConnector.value = connectorFromProject || updatedConnector
                root.$store.commit('project/setLoggedAddingConnector', channelConnector.value)
            }
        })

        const getChannelName = (channelType) => {
            const names = {
                [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                [CHANNEL_TYPES.maxChannel]: 'MAX',
                [CHANNEL_TYPES.maxBusiness]: 'MAX Business',
                [CHANNEL_TYPES.smsChannel]: 'SMS',
            }
            return names[channelType] || channelType
        }

        const onChannelConnected = async () => {
            // Для онбординга переключение шага идёт через emit, поэтому на долю секунды
            // показываем спиннер, пока родитель не перерисует следующий шаг.
            if (!props.redirectOnConnect) {
                isTransitioning.value = true
            }

            const projectId = project.value?.id
            if (projectId) {
                await refreshProjectAndAssignChannelSlots({
                    store: root.$store,
                    projectId,
                    extraConnectors: channelConnector.value ? [channelConnector.value] : [],
                })
            }

            if (props.redirectOnConnect) {
                try {
                    const connectors = project.value?.connectors || []
                    const whatsappBasis = connectors.find(
                        (c) => c && c.connectorType === CONNECTOR_TYPES.whatsappBasis
                    )
                    if (whatsappBasis && channelConnector.value) {
                        const channelId = channelConnector.value.channelId || channelConnector.value.connectorId
                        if (channelId) {
                            await root.$store.dispatch('project/updateConnectorSettings', {
                                connectorId: whatsappBasis.connectorId,
                                settings: {
                                    defaultChannel: { type: 'channel', id: channelId },
                                    yclientsChannel: { type: 'channel', id: channelId },
                                },
                            })
                        }
                    }
                } catch (e) {
                }
                router.router.push(`/project/${project.value.id}?showCascade=true`)
            }
            emit('channel-connected')
            emit('refresh-step')
        }

        return {
            channelConnector,
            channelSettings,
            providerType,
            onChannelConnected,
            isTransitioning,
            onBack,
        }
    },
}
</script>

<style lang="sass" scoped>
.content-wrapper
    margin-top: 0
    .content
        align-items: flex-start
        justify-content: center
        display: flex
        flex-direction: column
        width: 100%
        max-width: 100%
        min-width: 0
        box-sizing: border-box
</style>

